import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import { IItemForGetShortInfo, UserRoles, IGetRestaurantsShort, IGetRestaurantsShortArgs } from 'types';
import { getRestaurantsShortInfo } from 'backend/restaurants';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { useFetchData } from 'hooks/useFetchData';
import { Checkbox } from 'antd';
import Text from 'components/Text/Text';
import MultipleSelect from './components/MultipleSelect';
import SelectedItem from 'components/SelectedItem/SelectedItem';
import style from './RestaurantsMultipleSelect.module.scss';

interface IRestaurantsMultipleSelect {
	setIDsRestaurant: (value: string[]) => void;
	setIDsIsForceDisabledRestaurant: (value: string) => void;
	setFetchedRestaurants: (value: IItemForGetShortInfo[]) => void;
	IDSelectedCustomer: Nullable<string>;
	initialIDsSelectedRestaurant: string[];
	IDsSelectedRestaurant: string[];
	IDsIsForceDisabledRestaurant: string[];
}

const RestaurantsMultipleSelect: React.FC<IRestaurantsMultipleSelect> = ({
	IDsSelectedRestaurant,
	initialIDsSelectedRestaurant,
	IDsIsForceDisabledRestaurant,
	IDSelectedCustomer,
	setFetchedRestaurants,
	setIDsRestaurant,
	setIDsIsForceDisabledRestaurant,
}) => {
	const { loggedUser } = useContext<IAppContext>(AppContext);
	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);
	const { response, loading, refetch, responseError } = useFetchData<IGetRestaurantsShort, IGetRestaurantsShortArgs>(
		IDSelectedCustomer ? getRestaurantsShortInfo : null,
		{
			customerId: IDSelectedCustomer || '',
			menuApiVersion: 'v1',
		},
	);
	const [isFirstRender, setIsFirstRender] = useState<boolean>(true);

	const handleRemove = useCallback(
		(id: string) => {
			const updatedSelectedItems = IDsSelectedRestaurant.filter((el) => el !== id);
			setIDsRestaurant(updatedSelectedItems);
		},
		[IDsSelectedRestaurant, setIDsRestaurant],
	);

	const restaurantsList = useMemo(() => {
		return IDsSelectedRestaurant.map((el) => {
			const label = response?.data.restaurants.find((item) => item.id === el)?.name || '';
			const accessibleRestaurantIds = loggedUser.restaurants?.map((el) => el.id);
			const toggleIsForceDisabledRestaurant = () => setIDsIsForceDisabledRestaurant(el);

			return (
				<div className={style.selectedItemWrapper} key={el}>
					<SelectedItem
						className={style.selectedItem}
						label={label}
						onDelete={
							!hasAccess && !accessibleRestaurantIds?.includes(el) ? undefined : () => handleRemove(el)
						}
					/>
					<Checkbox
						className={style.checkboxOfSelectedItem}
						checked={IDsIsForceDisabledRestaurant.includes(el)}
						onChange={toggleIsForceDisabledRestaurant}
						disabled={!hasAccess && !accessibleRestaurantIds?.includes(el)}
						data-test="isForceDisabledCheckbox">
						<Text translationNameSpace="form" translationKey="isForceDisabledLabel" withTooltip={true} />
					</Checkbox>
				</div>
			);
		});
	}, [IDsSelectedRestaurant, IDsIsForceDisabledRestaurant, response, loggedUser, hasAccess, handleRemove]);

	useEffect(() => {
		refetch();
	}, [IDSelectedCustomer]);

	useEffect(() => {
		if (response) {
			const accessibleRestaurants = loggedUser.restaurants ? loggedUser.restaurants : [];
			const restaurants = hasAccess ? response.data.restaurants : accessibleRestaurants;
			setFetchedRestaurants(restaurants);
		}
		if (isFirstRender && response) {
			setIDsRestaurant(initialIDsSelectedRestaurant);
			setIsFirstRender(false);
		}
	}, [response, loggedUser, hasAccess, initialIDsSelectedRestaurant]);

	return (
		<div className={style.container} data-test="RestaurantsMultipleSelect">
			<div className={style.field}>
				<MultipleSelect
					data={response?.data.restaurants || []}
					isLoading={loading || isFirstRender}
					refetch={refetch}
					responseError={responseError}
					IDSelectedCustomer={IDSelectedCustomer}
					IDsSelectedRestaurant={IDsSelectedRestaurant}
					setIDsRestaurant={setIDsRestaurant}
				/>
			</div>
			<div className={style.groupsList}>{restaurantsList}</div>
		</div>
	);
};

export default React.memo(RestaurantsMultipleSelect);
